import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { handleApiError } from '@/shared/api/requests';
import { useProjectAreaTags, useProjectUsers } from '../hooks';
import type {
  CreatePunchWorkflowRunData,
  CreatePunchItemWorkflowData,
  PunchItemWorkflow,
} from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  workflow?: PunchItemWorkflow | null;
  draft?: CreatePunchItemWorkflowData | null;
  onSubmit: (data: CreatePunchWorkflowRunData) => Promise<unknown>;
}

export const PunchWorkflowLaunchDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  projectId,
  workflow = null,
  draft = null,
  onSubmit,
}) => {
  const { data: locations = [] } = useProjectAreaTags(projectId);
  const { data: users = [] } = useProjectUsers(projectId);
  const [location, setLocation] = useState<string>('none');
  const [manager, setManager] = useState<string>('none');
  const [approver, setApprover] = useState<string>('none');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => {
      const aLabel = a.layout_name ? `${a.layout_name} - ${a.name}` : a.name;
      const bLabel = b.layout_name ? `${b.layout_name} - ${b.name}` : b.name;
      return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' });
    }),
    [locations],
  );

  useEffect(() => {
    if (!open) return;
    setLocation('none');
    setManager('none');
    setApprover('none');
    setIsPrivate(false);
    setError(null);
  }, [open, workflow?.id, draft?.name]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!workflow && !draft) return;
    setSubmitting(true);
    setError(null);
    try {
      const defaults = {
        location: location === 'none' ? null : Number(location),
        punch_manager: manager === 'none' ? null : Number(manager),
        final_approver: approver === 'none' ? null : Number(approver),
        is_private: isPrivate,
      };
      await onSubmit(workflow ? {
        ...defaults,
        workflow: workflow.id,
      } : {
        ...defaults,
        workflow: null,
        name: draft!.name,
        description: draft!.description,
        is_template: draft!.is_template,
        steps: draft!.steps,
      });
      onOpenChange(false);
    } catch (submissionError) {
      setError(handleApiError(submissionError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Where is this workflow happening?</DialogTitle>
          <DialogDescription>
            Choose the location and the people responsible for overseeing the work. The first group of tasks will be assigned when you start.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="rounded-xl border border-border bg-muted p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Workflow</p>
            <p className="mt-1 font-display text-lg font-bold">
              {workflow?.name ?? draft?.name}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {workflow?.stage_count
                ?? new Set(draft?.steps.map((step) => step.stage) ?? []).size} work groups ·{' '}
              {workflow?.steps.length ?? draft?.steps.length ?? 0} tasks
            </p>
          </div>
          <div className="space-y-2">
            <Label>Location</Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger><SelectValue placeholder="No location" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No location</SelectItem>
                {sortedLocations.map((option) => (
                  <SelectItem key={option.id} value={String(option.id)}>
                    {option.layout_name ? `${option.layout_name} - ${option.name}` : option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Person coordinating the work</Label>
              <Select value={manager} onValueChange={setManager}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.map((user) => <SelectItem key={user.id} value={String(user.id)}>{`${user.first_name} ${user.last_name}`.trim() || user.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Who gives final sign-off?</Label>
              <Select value={approver} onValueChange={setApprover}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.map((user) => <SelectItem key={user.id} value={String(user.id)}>{`${user.first_name} ${user.last_name}`.trim() || user.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-3 rounded-xl border border-border bg-muted px-4 py-3 text-sm font-medium">
            <Checkbox checked={isPrivate} onCheckedChange={(checked) => setIsPrivate(checked === true)} />
            Limit these tasks to selected project members
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={(!workflow && !draft) || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Start Workflow
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
